import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { toast } from "sonner";
import { Home, Radio, Trophy, Bell, User, Sun, Moon, Wifi, WifiOff, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { WalkieEngine, outboxAll, outboxDelete } from "@/lib/walkieRtc";

const Ctx = createContext(null);
export const useWalkie = () => useContext(Ctx);
export const wtErr = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";

const CSS = `
.wt-dark{--bg:#07070c;--fg:#f4f6ff;--card:rgba(255,255,255,.05);--line:rgba(255,255,255,.1);--muted:rgba(244,246,255,.55);--nav:rgba(7,7,12,.82)}
.wt-light{--bg:#f3f5fb;--fg:#0c0d16;--card:#ffffff;--line:rgba(12,13,22,.1);--muted:rgba(12,13,22,.55);--nav:rgba(255,255,255,.9)}
.wt-root{background:var(--bg);color:var(--fg)}
.wt-card{background:var(--card);border:1px solid var(--line)}
.wt-muted{color:var(--muted)}
.wt-line{border-color:var(--line)}
.wt-input{background:var(--card);border:1px solid var(--line);color:var(--fg)}
`;

export function Avatar({ p, size = 40 }) {
  const ring = p?.decoration === "dec_fire" ? "ring-[#FF6A00]" : p?.decoration === "dec_neon" ? "ring-[#00F0FF]" : p?.decoration === "dec_crown" ? "ring-[#FFE300]" : p?.premium ? "ring-[#FF2BD6]" : "ring-transparent";
  return (
    <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
      {p?.avatar_url ? <img src={p.avatar_url} alt="" className={`w-full h-full rounded-full object-cover ring-2 ${ring}`} />
        : <span className={`w-full h-full rounded-full flex items-center justify-center font-black ring-2 ${ring} bg-gradient-to-br from-[#00F0FF] to-[#FF2BD6] text-black`} style={{ fontSize: size * 0.4 }}>{(p?.display_name || "?")[0].toUpperCase()}</span>}
      {p?.decoration === "dec_crown" && <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px]">👑</span>}
      {p?.online && <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[#39FF14] ring-2 ring-[var(--bg)]" />}
    </span>
  );
}

function useOutbox(status) {
  const [count, setCount] = useState(0);
  const refresh = useCallback(() => outboxAll().then((a) => setCount(a.length)).catch(() => {}), []);
  const flush = useCallback(async () => {
    const items = await outboxAll().catch(() => []);
    let sent = 0;
    for (const it of items) {
      const fd = new FormData();
      fd.append("file", it.blob, "voice.webm"); fd.append("duration", String(it.duration));
      fd.append("client_msg_id", it.id); fd.append("recorded_offline", "true");
      try { await api.post(`/walkie/channels/${it.channel_id}/voice`, fd); await outboxDelete(it.id); sent++; }
      catch (e) { if (e?.response && e.response.status < 500) await outboxDelete(it.id); }
    }
    if (sent) toast.success(`Sent ${sent} queued voice message${sent > 1 ? "s" : ""}`);
    refresh();
  }, [refresh]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (status === "online" && localStorage.getItem("wt_autosend") === "1") flush();
  }, [status, flush]);
  return { count, refresh, flush };
}

export default function WalkieLayout() {
  const { user, loading } = useAuth();
  const [me, setMe] = useState(null);
  const [status, setStatus] = useState(navigator.onLine ? "connecting" : "offline");
  const [dark, setDark] = useState(() => localStorage.getItem("wt_theme") !== "light");
  const [unread, setUnread] = useState(0);
  const engineRef = useRef(null);
  const listeners = useRef(new Set());
  const outbox = useOutbox(status);

  const reloadMe = useCallback(() => api.get("/walkie/me").then((r) => setMe(r.data)).catch((e) => { if (e?.response?.status === 403) setMe({ suspended: true }); }), []);
  const reloadUnread = useCallback(() => api.get("/walkie/notifications").then((r) => setUnread(r.data.unread)).catch(() => {}), []);

  useEffect(() => { document.title = "Jixxeo Walkie Talkies"; }, []);
  useEffect(() => {
    if (!user) return;
    reloadMe(); reloadUnread();
    const eng = new WalkieEngine((type, ...args) => {
      if (type === "status") setStatus(args[0]);
      listeners.current.forEach((fn) => fn(type, ...args));
    });
    engineRef.current = eng;
    eng.connect();
    const off = () => setStatus("offline");
    window.addEventListener("offline", off);
    const t = setInterval(reloadUnread, 30000);
    return () => { eng.destroy(); window.removeEventListener("offline", off); clearInterval(t); };
  }, [user, reloadMe, reloadUnread]);

  const subscribe = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);
  const toggleDark = () => setDark((d) => { localStorage.setItem("wt_theme", d ? "light" : "dark"); return !d; });

  const ctx = { me, reloadMe, engine: engineRef.current, subscribe, status, dark, outbox, unread, reloadUnread };

  return (
    <div className={`wt-root min-h-screen flex flex-col ${dark ? "wt-dark" : "wt-light"}`} style={{ fontFamily: "'Outfit',ui-sans-serif" }} data-testid="walkie-layout">
      <style>{CSS}</style>
      <header className="sticky top-0 z-40 backdrop-blur-xl border-b wt-line" style={{ background: "var(--nav)" }}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/browse" className="text-[10px] mono uppercase tracking-widest wt-muted hover:opacity-100" data-testid="walkie-back">← Apps</Link>
          <div className="flex-1 text-center font-black text-lg leading-none">
            Jixxeo <span className="bg-gradient-to-r from-[#FF6A00] via-[#FF2BD6] to-[#00F0FF] bg-clip-text text-transparent">Walkie Talkies</span>
          </div>
          <StatusPill status={status} />
          <button onClick={toggleDark} data-testid="walkie-theme-toggle" aria-label="Toggle theme" className="w-8 h-8 rounded-full wt-card flex items-center justify-center">
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
        {status !== "online" && status !== "connecting" && (
          <div className="text-center text-[11px] py-1 bg-[#FF6A00]/15 text-[#FF6A00]" data-testid="walkie-offline-banner">
            {status === "reconnecting" ? "Reconnecting…" : "You're offline."} Live voice needs internet — you can still record voice messages to send later.
          </div>
        )}
      </header>
      <main className="flex-1 pb-24">
        <div className="max-w-2xl mx-auto px-4 py-4">
          {!user && !loading ? <LoginGate /> : me?.suspended ? (
            <div className="text-center py-20 text-[#FF003C]" data-testid="walkie-suspended">Your Walkie Talkies account is suspended.</div>
          ) : !me ? <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF2BD6]" /></div>
            : <Ctx.Provider value={ctx}><Outlet /></Ctx.Provider>}
        </div>
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl border-t wt-line" style={{ background: "var(--nav)" }}>
        <div className="max-w-2xl mx-auto grid grid-cols-5">
          <Tab to="/walkie" end icon={<Home className="w-5 h-5" />} label="Home" testId="walkie-tab-home" />
          <Tab to="/walkie/channels" icon={<Radio className="w-5 h-5" />} label="Channels" testId="walkie-tab-channels" />
          <Tab to="/walkie/leaderboard" icon={<Trophy className="w-5 h-5" />} label="Ranks" testId="walkie-tab-leaderboard" />
          <Tab to="/walkie/notifications" icon={<span className="relative"><Bell className="w-5 h-5" />{unread > 0 && <span className="absolute -top-1.5 -right-2 bg-[#FF003C] text-white text-[9px] font-black rounded-full px-1" data-testid="walkie-unread-badge">{unread}</span>}</span>} label="Alerts" testId="walkie-tab-notifications" />
          <Tab to="/walkie/me" icon={<User className="w-5 h-5" />} label="Profile" testId="walkie-tab-profile" />
        </div>
      </nav>
    </div>
  );
}

function StatusPill({ status }) {
  const on = status === "online";
  return (
    <span data-testid="walkie-status" data-status={status}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded-full ${on ? "bg-[#39FF14]/15 text-[#39FF14]" : "bg-[#FF6A00]/15 text-[#FF6A00]"}`}>
      {on ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />} {on ? "Online" : status === "connecting" ? "…" : "Offline"}
    </span>
  );
}

function LoginGate() {
  return (
    <div className="text-center py-16" data-testid="walkie-login-gate">
      <Radio className="w-14 h-14 mx-auto text-[#FF2BD6]" />
      <h1 className="font-black text-3xl mt-4">Push to talk, anywhere.</h1>
      <p className="wt-muted mt-2 text-sm max-w-sm mx-auto">Talk across cities, states and countries over the internet. Create a free Jixxeo account or sign in to start.</p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/register?next=/walkie" data-testid="walkie-signup-btn" className="bg-[#FF2BD6] hover:opacity-90 text-black font-black px-6 py-3 rounded-full">Create free account</Link>
        <Link to="/login?next=/walkie" data-testid="walkie-login-btn" className="wt-card font-black px-6 py-3 rounded-full">Sign in</Link>
      </div>
      <p className="text-[11px] wt-muted mt-3">One Jixxeo account works across every Jixxeo app.</p>
    </div>
  );
}

function Tab({ to, icon, label, testId, end }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) => "flex flex-col items-center gap-0.5 py-2.5 text-[10px] uppercase tracking-widest transition-colors " + (isActive ? "text-[#FF2BD6]" : "wt-muted")}>
      {icon}<span className="font-bold">{label}</span>
    </NavLink>
  );
}

export const notify = { err: (e) => toast.error(wtErr(e)) };
